import { CONFIG } from '../config.js';
import { safeFetchClient } from '../utils/safe-fetch-client.js';
import { getCurrentSession } from './auth.service.js';
import { listAdelantos as repoListAdelantos } from '../infrastructure/adelantos.repository.js';
import { listOtrosDescuentos as repoListOtrosDescuentos } from '../infrastructure/otros-descuentos.repository.js';
import { MOTIVO_MAX_LEN } from '../shared/timesheet-policy.js';

/**
 * Servicio de adelantos (S4, design.md AD8/AD9) y de otros descuentos (spec
 * liquidacion-otros-descuentos, mismo transporte). Lecturas browser-directo
 * bajo RLS (`adelantos.repository.js`); altas/anulaciones vía `POST
 * /api/payroll-movimientos` con el `access_token` de la sesión de staff
 * activa como Bearer — mismo patrón que `admin-liquidacion.service.js`'s
 * `anularLiquidacion` (el endpoint corre server-side con la service_role
 * key porque las RPCs son service_role-only).
 */

const ERROR_MESSAGES = {
    unauthorized: 'Tu sesión no es válida. Volvé a iniciar sesión.',
    forbidden: 'No tenés permiso para cargar adelantos.',
    accion_invalida: 'Acción inválida.',
    empleado_id_requerido: 'Elegí un empleado.',
    fecha_invalida: 'La fecha no es válida.',
    monto_invalido: 'El monto debe ser un número mayor a cero.',
    empleado_inexistente: 'Ese empleado no existe.',
    // AD12/spec "Advance refused inside an already-settled period" — copia
    // literal del texto de tasks.md S4.11.
    periodo_ya_liquidado: 'Ese período ya está liquidado; cargalo con fecha del período actual.',
    adelanto_id_requerido: 'No se pudo identificar el adelanto a anular.',
    motivo_requerido: 'Ingresá un motivo para anular el adelanto.',
    motivo_demasiado_largo: 'El motivo es demasiado largo.',
    adelanto_inexistente: 'Ese adelanto no existe.',
    adelanto_ya_anulado: 'Este adelanto ya fue anulado anteriormente.',
    adelanto_liquidado: 'Este adelanto ya fue consumido por una liquidación confirmada y no se puede anular.',
    no_disponible: 'El servicio no está disponible en este momento. No se registró nada; probá de nuevo en unos minutos.',
    error_interno: 'Ocurrió un error. Probá de nuevo.',
};

/**
 * Copia de los otros descuentos (spec liquidacion-otros-descuentos, D2): el
 * transporte es el mismo que el de adelantos, pero los mensajes que puede
 * devolver una acción de descuento nunca hablan de "adelanto" (los códigos
 * `adelanto_*` heredados del spread no los devuelve nunca el endpoint para
 * `descuento_*`) y el concepto se nombra siempre "otro descuento".
 */
const ERROR_MESSAGES_OTROS_DESCUENTOS = {
    ...ERROR_MESSAGES,
    forbidden: 'No tenés permiso para cargar descuentos.',
    fecha_requerida: 'Completá la fecha.',
    concepto_requerido: 'Escribí el concepto del descuento.',
    concepto_largo: `El concepto es demasiado largo (máximo ${MOTIVO_MAX_LEN} caracteres).`,
    descuento_id_requerido: 'No se pudo identificar el otro descuento a anular.',
    motivo_requerido: 'Ingresá un motivo para anular el otro descuento.',
    descuento_inexistente: 'Ese otro descuento no existe.',
    descuento_ya_anulado: 'Este otro descuento ya fue anulado anteriormente.',
    descuento_liquidado: 'Ese otro descuento ya entró en una liquidación; anulá la liquidación primero.',
};

/**
 * @param {string|undefined} code
 * @param {Record<string, string>} [mensajes]
 * @returns {string}
 */
function mapMensaje(code, mensajes = ERROR_MESSAGES) {
    return mensajes[code] ?? mensajes.error_interno;
}

/**
 * @typedef {{ ok: true, adelantoId?: string, descuentoId?: string } | {
 *   ok: false,
 *   kind: 'auth'|'validacion'|'incierto'|'error',
 *   mensaje: string,
 *   errorCode?: string,
 * }} AdelantoMovimientoResult
 */

/**
 * POST /api/payroll-movimientos con el Bearer token de la sesión de staff
 * activa. Único punto de fetch de este servicio para escrituras. `mensajes`
 * e `idDeRespuesta` son por concepto (adelanto u otro descuento); el
 * transporte y la clasificación auth/validacion/incierto/error son uno solo.
 * @param {object} body
 * @param {{ mensajes?: Record<string, string>, idDeRespuesta?: (responseBody: object) => object }} [opciones]
 * @returns {Promise<AdelantoMovimientoResult>}
 */
async function postMovimiento(body, { mensajes = ERROR_MESSAGES, idDeRespuesta = (r) => ({ adelantoId: r.adelanto_id }) } = {}) {
    const session = await getCurrentSession();
    if (!session?.access_token) {
        return { ok: false, kind: 'auth', mensaje: mensajes.unauthorized };
    }

    let res;
    try {
        res = await safeFetchClient(CONFIG.API.PAYROLL_MOVIMIENTOS, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${session.access_token}`,
            },
            body: JSON.stringify(body),
        });
    } catch {
        // Falla de transporte AMBIGUA por definición (mismo criterio que
        // anularLiquidacion): no hay forma de distinguir "la RPC nunca
        // corrió" de "corrió y la respuesta se perdió" — nunca "error, probá
        // de nuevo" a ciegas para una escritura.
        return {
            ok: false,
            kind: 'incierto',
            mensaje: 'Se cortó la conexión. No sabemos si se aplicó — revisá la lista antes de reintentar.',
        };
    }

    const responseBody = await res.json().catch(() => ({}));

    if (res.ok) {
        return { ok: true, ...idDeRespuesta(responseBody) };
    }
    if (res.status === 401 || res.status === 403) {
        return { ok: false, kind: 'auth', mensaje: mapMensaje(responseBody.error, mensajes), errorCode: responseBody.error };
    }
    // 400/404/409 son los rechazos de negocio de las RPCs de adelantos y de otros descuentos
    // — SIEMPRE 'validacion' (no hay noción de batch/escritura parcial acá,
    // mismo criterio que anularLiquidacion).
    if (res.status === 400 || res.status === 404 || res.status === 409) {
        return { ok: false, kind: 'validacion', mensaje: mapMensaje(responseBody.error, mensajes), errorCode: responseBody.error };
    }

    // The one 5xx that is NOT ambiguous: api/payroll-movimientos.js answers
    // 503 { error: 'no_disponible' } before any RPC runs (no Supabase
    // client), so nothing was written: a clean error, the caller keeps the
    // typed values for a retry. A 5xx without that exact body stays below.
    if (res.status >= 500 && responseBody.error === 'no_disponible') {
        return { ok: false, kind: 'error', mensaje: mapMensaje(responseBody.error, mensajes), errorCode: responseBody.error };
    }

    // Any other 5xx is AMBIGUOUS for a write: a gateway 502/504 or a 500 after a
    // lost DB response can arrive once the RPC (adelanto or otro descuento) already committed.
    // Never a clean "probá de nuevo" — the caller must refresh the list
    // before letting the admin resubmit (same criterion as the transport
    // failure above).
    if (res.status >= 500) {
        return {
            ok: false,
            kind: 'incierto',
            mensaje: 'El servidor no respondió bien. No sabemos si se aplicó — revisá la lista antes de reintentar.',
            errorCode: responseBody.error,
        };
    }

    return { ok: false, kind: 'error', mensaje: mapMensaje(responseBody.error, mensajes), errorCode: responseBody.error };
}

/**
 * Alta de un adelanto (spec `payroll-advances`, "Creating an Advance").
 * @param {{ empleadoId: string, fecha: string, monto: number, nota?: string|null }} params
 * @returns {Promise<AdelantoMovimientoResult>}
 */
export async function crearAdelanto({ empleadoId, fecha, monto, nota } = {}) {
    return postMovimiento({ action: 'adelanto_crear', empleado_id: empleadoId, fecha, monto, nota: nota ?? null });
}

/**
 * Anulación auditada de un adelanto (spec `payroll-advances`, "Voiding an
 * Advance"). `motivo` se valida server-side (el endpoint revalida igual) —
 * este servicio no duplica esa validación de UX, a diferencia de
 * `anularLiquidacion`, para no bifurcar el mensaje en dos lugares distintos
 * del mismo error de negocio.
 * @param {{ adelantoId: string, motivo: string }} params
 * @returns {Promise<AdelantoMovimientoResult>}
 */
export async function anularAdelanto({ adelantoId, motivo } = {}) {
    return postMovimiento({ action: 'adelanto_anular', adelanto_id: adelantoId, motivo });
}

/**
 * Lista de adelantos (activos y anulados) para un conjunto de empleados y/o
 * rango de fechas — lectura de PANTALLA vía RLS (`adelantos.repository.js`),
 * thin pass-through sin try/catch a propósito, mismo criterio que
 * `loadLiquidacionesHistorial`: el caller (`js/admin.js`) atrapa una falla
 * real de Supabase y degrada la pantalla que corresponda.
 * @param {{ empleadoIds?: string[], desde?: string, hasta?: string }} [params]
 * @returns {Promise<Array<object>>}
 * @throws {Error} si Supabase responde con error real
 */
export async function loadAdelantos(params = {}) {
    return repoListAdelantos(params);
}

const OPCIONES_OTROS_DESCUENTOS = {
    mensajes: ERROR_MESSAGES_OTROS_DESCUENTOS,
    idDeRespuesta: (r) => ({ descuentoId: r.descuento_id }),
};

/**
 * Alta de un otro descuento (spec liquidacion-otros-descuentos, B1).
 * @param {{ empleadoId: string, fecha: string, monto: number, concepto: string }} params
 * @returns {Promise<{ ok: true, descuentoId?: string } | { ok: false, kind: 'auth'|'validacion'|'incierto'|'error', mensaje: string, errorCode?: string }>}
 */
export async function crearOtroDescuento({ empleadoId, fecha, monto, concepto } = {}) {
    return postMovimiento({ action: 'descuento_crear', empleado_id: empleadoId, fecha, monto, concepto }, OPCIONES_OTROS_DESCUENTOS);
}

/**
 * Anulación auditada de un otro descuento (spec liquidacion-otros-descuentos, B2).
 * @param {{ descuentoId: string, motivo: string }} params
 * @returns {Promise<{ ok: true, descuentoId?: string } | { ok: false, kind: 'auth'|'validacion'|'incierto'|'error', mensaje: string, errorCode?: string }>}
 */
export async function anularOtroDescuento({ descuentoId, motivo } = {}) {
    return postMovimiento({ action: 'descuento_anular', descuento_id: descuentoId, motivo }, OPCIONES_OTROS_DESCUENTOS);
}

/**
 * Lista de otros descuentos (activos y anulados), lectura de PANTALLA vía RLS.
 * Thin pass-through, mismo criterio que `loadAdelantos`: el caller atrapa una
 * falla real de Supabase y degrada la pantalla.
 * @param {{ empleadoIds?: string[], desde?: string, hasta?: string }} [params]
 * @returns {Promise<Array<object>>}
 * @throws {Error} si Supabase responde con error real
 */
export async function loadOtrosDescuentos(params = {}) {
    return repoListOtrosDescuentos(params);
}
